import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

// Add CSS for register link hover effect
const styles = `
  .register-link:hover {
    color: #495057;
  }
`;

const LoginForm = () => {
  // Add the styles to the document
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [csrfToken, setCsrfToken] = useState('');

  // Fetch CSRF token when component mounts
  useEffect(() => {
    // Function to fetch CSRF token
    const fetchCSRFToken = async () => {
      try {
        // Make a GET request to the server to get the CSRF cookie
        let tokenFetched = false;
        
        // Try with fetch first
        try {
          const response = await fetch(`${API_BASE_URL}/login/`, {
            method: 'GET',
            credentials: 'include', // Include cookies in the request
          });
          
          if (response.ok) {
            tokenFetched = true;
          }
        } catch (fetchError) {
          // If fetch fails, try with axios
          try {
            const response = await axios.get(`${API_BASE_URL}/login/`, {
              withCredentials: true
            });
            
            if (response.status === 200) {
              tokenFetched = true;
            }
          } catch (axiosError) {
            // Silent fail - will try again later if needed
          }
        }
        
        // After the request, the CSRF cookie should be set
        const token = getCSRFToken();
        if (token) {
          setCsrfToken(token);
        } else {
          // Try again after a short delay
          setTimeout(fetchCSRFToken, 1000);
        }
      } catch (error) {
        // Silent fail
      }
    };

    fetchCSRFToken();
  }, []);

  // Function to get CSRF token from cookies
  const getCSRFToken = () => {
    const name = 'csrftoken=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length, cookie.length);
      }
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure we have a CSRF token
      let token = csrfToken || getCSRFToken();
      
      // If no token, try to fetch it again
      if (!token) {
        try {
          const response = await fetch(`${API_BASE_URL}/login/`, {
            method: 'GET',
            credentials: 'include',
          });
          
          if (response.ok) {
            token = getCSRFToken();
          }
        } catch (error) {
          // Silent fail
        }
      }
      
      if (!token) {
        setError('登录失败，请刷新页面重试');
        return;
      }
      
      // Determine if we're in production or development
      const isProduction = process.env.NODE_ENV === 'production';
      
      // In production, we might need to use the full URL
      const loginUrl = isProduction && !API_BASE_URL 
        ? `${window.location.origin}/login/` 
        : `${API_BASE_URL}/login/`;
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': token,
        },
        credentials: 'include', // Include cookies in the request
        body: JSON.stringify({ username, password }),
      });
      if (response.ok) {
        const data = await response.json();
        // No need to store token in localStorage anymore
        localStorage.setItem('username', data.username);
        localStorage.setItem('userid', data.userid);
        setError(data.message);
        
        // Dispatch loginStatusChanged event
        window.dispatchEvent(new Event('loginStatusChanged'));
        
        let pre = new URLSearchParams(window.location.search).get('pre');
        pre = pre ? pre : '';
        window.location.href = `/${pre}`;

      } else {
        // If fetch fails, try with axios as a fallback
        
        // Ensure we still have a valid token for axios request
        token = token || getCSRFToken();
        if (!token) {
          setError('登录失败，请刷新页面重试');
          return;
        }
        
        try {
          // Use the same URL determination for axios
          const axiosLoginUrl = isProduction && !API_BASE_URL 
            ? `${window.location.origin}/login/` 
            : `${API_BASE_URL}/login/`;
          
          const axiosResponse = await axios.post(axiosLoginUrl, 
            { username, password },
            { 
              withCredentials: true,
              headers: {
                'X-CSRFToken': token,
                'Content-Type': 'application/json',
              }
            }
          );
          
          if (axiosResponse.status === 200) {
            const data = axiosResponse.data;
            // No need to store token in localStorage anymore
            localStorage.setItem('username', data.username);
            localStorage.setItem('userid', data.userid);
            setError(data.message);
            
            // Dispatch loginStatusChanged event
            window.dispatchEvent(new Event('loginStatusChanged'));
            
            let pre = new URLSearchParams(window.location.search).get('pre');
            pre = pre ? pre : '';
            window.location.href = `/${pre}`;
          } else {
            setError('登录失败，请检查用户名或密码');
          }
        } catch (axiosError) {
          if (axiosError.response) {
            setError(axiosError.response.data.error || '登录失败，请检查用户名或密码');
          } else {
            setError('登录失败，请检查用户名或密码');
          }
        }
      }
    } catch (error) {
      // Silent fail
      setError('登录失败，请检查用户名或密码');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="logreg-container mt-5 p-4 bg-white shadow rounded">
        <div className="mb-4 position-relative">
          <h2 className="text-center mb-0">登录</h2>
          <span 
            className="register-link position-absolute" 
            onClick={() => window.location.href = '/register'}
            style={{
              fontSize: '14px',
              color: '#6c757d',
              cursor: 'pointer',
              textDecoration: 'underline',
              right: '0',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = '#495057';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = '#6c757d';
            }}
          >
            点击注册
          </span>
        </div>
        <div className="form-group">
          <label htmlFor="username">用户名</label>
          <input id="username" type="text" value={username} className="form-control" onChange={(e) => setUsername(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="password">密码</label>
          <input id="password" type="password" value={password} className="form-control" onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="button-container">
          <button type="submit" className="btn btn-block logreg-submit">登录</button>
        </div>
        {error && <p>{error}</p>}
      </div>
    </form>
  );
};


export default LoginForm;
